import { ReactNode } from 'react'
import { PreviewVideo } from 'amazon-chime-sdk-component-library-react'
import { Typography } from '@/components/atoms/Typography/Typography'
import { VideoControlsGroup } from '@/components/molecules/VideoControlsGroup'
import { DEVICE_LABELS } from '@/constants/meeting'
import type { VideoState } from '@hooks/useVideoState'
import { Button } from '@/components/ui/Button/Button'
import { ChevronLeftIcon } from 'lucide-react'
import { marketplaceConversationUrl } from '@/utils/conversation.utils'

type Device = {
  id: string
  label: string
}

type VideoSetupPanelProps = {
  isVideoActive: boolean
  isMicrophoneActive: boolean
  noCameraAvailable: boolean
  noMicrophoneAvailable: boolean
  onToggleVideo: () => void
  onToggleMicrophone: () => void
  videoInputs: Device[]
  audioInputs: Device[]
  audioOutputs: Device[]
  selectedVideoInput?: string
  selectedAudioInput?: string
  selectedAudioOutput?: string
  onVideoInputChange: (id: string) => void
  onAudioInputChange: (id: string) => void
  onAudioOutputChange: (id: string) => void
  audioLevel?: number
  videoState?: VideoState
  previewComponent?: ReactNode
  isCameraPermissionDenied?: boolean
  isMicrophonePermissionDenied?: boolean
  conversationId: string
}

export const VideoSetupPanel = ({
  isVideoActive,
  isMicrophoneActive,
  noCameraAvailable,
  noMicrophoneAvailable,
  onToggleVideo,
  onToggleMicrophone,
  videoInputs,
  audioInputs,
  audioOutputs,
  selectedVideoInput,
  selectedAudioInput,
  selectedAudioOutput,
  onVideoInputChange,
  onAudioInputChange,
  onAudioOutputChange,
  audioLevel = 0,
  videoState = 'inactive',
  previewComponent,
  isCameraPermissionDenied = false,
  isMicrophonePermissionDenied = false,
  conversationId
}: VideoSetupPanelProps) => {
  const conversationUrl = marketplaceConversationUrl(conversationId)
  return (
    <div className="flex flex-col gap-6">
      <Button asChild variant="link" className="p-0 self-start lg:hidden">
        <a href={conversationUrl}>
          <ChevronLeftIcon /> Retour sur la conversation
        </a>
      </Button>

      {/* Video Preview with Controls */}
      <div className="relative aspect-video w-full h-[40dvh] lg:h-[316px] overflow-hidden rounded-lg bg-gray-200">
        {isVideoActive ? (
          <div className="absolute inset-0 flex items-center justify-center">
            {previewComponent ?? (
              <PreviewVideo className="!absolute !inset-0 !h-full !w-full !object-cover [&>video]:!absolute [&>video]:!inset-0 [&>video]:!h-full [&>video]:!w-full [&>video]:!object-cover" />
            )}
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Typography type="body" size="md" className="text-center">
              {DEVICE_LABELS.CAMERA_DISABLED}
            </Typography>
          </div>
        )}

        {/* Video Controls - Absolute positioned at bottom center */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
          <VideoControlsGroup
            isVideoActive={isVideoActive && !noCameraAvailable}
            isMicrophoneActive={isMicrophoneActive && !noMicrophoneAvailable}
            onToggleVideo={onToggleVideo}
            onToggleMicrophone={onToggleMicrophone}
            audioLevel={audioLevel}
            videoState={videoState}
            isCameraPermissionDenied={isCameraPermissionDenied}
            isMicrophonePermissionDenied={isMicrophonePermissionDenied}
            videoInputs={videoInputs}
            audioInputs={audioInputs}
            audioOutputs={audioOutputs}
            selectedVideoInput={selectedVideoInput}
            selectedAudioInput={selectedAudioInput}
            selectedAudioOutput={selectedAudioOutput}
            onVideoInputChange={onVideoInputChange}
            onAudioInputChange={onAudioInputChange}
            onAudioOutputChange={onAudioOutputChange}
          />
        </div>
      </div>
    </div>
  )
}
